import { useState } from 'react'
import { SERVICES, REGIONS, DEFAULT_SERVICES, DEFAULT_REGION } from '../constants.js'
import { saveServices, saveRegion, saveSetupDone } from '../storage.js'
import { track } from '../analytics.js'
import flowIcon from '@assets/image_1777447102475.png'

const STEP_COUNT = 3

// Guess region from browser locale (e.g. "en-US" → "US")
function guessRegion() {
  try {
    const lang = navigator.language || navigator.languages?.[0] || ''
    const code = lang.split('-')[1]?.toUpperCase()
    return REGIONS.find(r => r.code === code) ? code : DEFAULT_REGION
  } catch {
    return DEFAULT_REGION
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export default function Onboarding({ onComplete }) {
  const [step, setStep]         = useState(0)
  const [selSvc, setSelSvc]     = useState(['netflix', 'hbo', 'prime', 'hulu', 'apple'])
  const [selRegion, setSelRegion] = useState(guessRegion)
  const [email, setEmail]       = useState('')
  const [emailError, setEmailError] = useState('')

  const toggleSvc = (id) => {
    setSelSvc(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id])
  }

  const finish = (emailValue = '') => {
    const services = selSvc.length ? selSvc : DEFAULT_SERVICES.slice()
    saveServices(services)
    saveRegion(selRegion)
    saveSetupDone()
    track('onboarding_completed', {
      services,
      region:         selRegion,
      email_provided: !!emailValue,
      skipped:        false,
    })
    onComplete(services, selRegion)
  }

  const skip = () => {
    saveServices(DEFAULT_SERVICES.slice())
    saveRegion(DEFAULT_REGION)
    saveSetupDone()
    track('onboarding_completed', {
      services:       DEFAULT_SERVICES,
      region:         DEFAULT_REGION,
      email_provided: false,
      skipped:        true,
    })
    onComplete(DEFAULT_SERVICES.slice(), DEFAULT_REGION)
  }

  const handleSaveEmail = () => {
    const trimmed = email.trim()
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      setEmailError('Please enter a valid email address.')
      return
    }
    if (trimmed) {
      fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, region: selRegion, streaming_services: selSvc }),
      }).catch(() => {})
    }
    finish(trimmed)
  }

  const regionObj = REGIONS.find(r => r.code === selRegion) || REGIONS[0]

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
    }}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: 'rgba(6,14,20,0.88)',
        border: '1px solid rgba(80,160,220,0.2)',
        borderRadius: 24,
        backdropFilter: 'blur(32px)',
        WebkitBackdropFilter: 'blur(32px)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(80,160,220,0.08)',
        overflow: 'hidden',
        animation: 'fadeUp 0.4s ease',
        marginTop: 'auto', marginBottom: 'auto',
        position: 'relative',
      }}>

        {/* Progress bar */}
        <div style={{ height: 3, background: 'rgba(255,255,255,0.06)' }}>
          <div style={{
            height: '100%',
            width: `${((step + 1) / (STEP_COUNT + 2)) * 100}%`,
            background: 'linear-gradient(90deg,#3b8ce6,#5db0d4)',
            transition: 'width 0.4s ease',
            borderRadius: 3,
          }} />
        </div>

        {/* Skip button */}
        {step > 0 && (
          <button
            onClick={skip}
            style={{
              position: 'absolute', top: 16, right: 18,
              background: 'transparent', border: 'none',
              color: 'rgba(140,195,225,0.45)', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.05em',
              padding: '4px 8px', borderRadius: 6,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Skip →
          </button>
        )}

        <div style={{ padding: '32px 28px 36px' }}>

          {/* ── STEP 0: Welcome ── */}
          {step === 0 && (
            <div style={{ textAlign: 'center', animation: 'fadeUp 0.35s ease' }}>
              <img src={flowIcon} alt="Flow" width="80" height="80"
                style={{ width: 80, height: 80, borderRadius: 18, marginBottom: 20,
                  boxShadow: '0 8px 32px rgba(60,140,220,0.4)',
                  animation: 'logoFloat 4s ease-in-out infinite' }} />
              <h1 style={{ fontSize: 28, fontWeight: 900, color: '#dceaf2',
                letterSpacing: '-0.8px', marginBottom: 10 }}>
                Welcome to Flow
              </h1>
              <p style={{ fontSize: 15, color: 'rgba(180,215,235,0.7)',
                lineHeight: 1.6, marginBottom: 32, maxWidth: 320, margin: '0 auto 32px' }}>
                Your personal streaming concierge. Let's take 30 seconds to personalise your experience.
              </p>
              <button onClick={() => setStep(1)} style={btnPrimary}>
                Get Started →
              </button>
              <button onClick={skip} style={{ ...btnSecondaryWide, marginTop: 12 }}>
                Skip — use defaults
              </button>
            </div>
          )}

          {/* ── STEP 1: Region ── */}
          {step === 1 && (
            <div style={{ animation: 'fadeUp 0.35s ease' }}>
              <StepLabel n={1} total={STEP_COUNT} />
              <h2 style={heading}>Where are you watching from?</h2>
              <p style={subtext}>This affects which services carry each title in your area.</p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8, marginBottom: 28,
                maxHeight: 280, overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingRight: 4,
              }}>
                {REGIONS.map(r => {
                  const on = selRegion === r.code
                  return (
                    <button key={r.code} onClick={() => setSelRegion(r.code)} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: on ? 'rgba(59,140,230,0.18)' : 'rgba(255,255,255,0.03)',
                      border: `1.5px solid ${on ? 'rgba(59,140,230,0.55)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 12, padding: '10px 12px',
                      cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 0.18s',
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                      <img
                        src={`https://flagcdn.com/w40/${r.code.toLowerCase()}.png`}
                        width="28" height="21"
                        alt={r.flag}
                        style={{ flexShrink: 0, borderRadius: 3, objectFit: 'cover', display: 'block' }}
                      />
                      <span style={{
                        fontSize: 13, fontWeight: 600, color: on ? '#dceaf2' : 'rgba(180,215,235,0.55)',
                        textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{r.name}</span>
                      {on && <div style={{ marginLeft: 'auto', color: '#5db0d4', fontSize: 14, flexShrink: 0 }}>✓</div>}
                    </button>
                  )
                })}
              </div>
              <button onClick={() => setStep(2)} style={btnPrimary}>Continue →</button>
            </div>
          )}

          {/* ── STEP 2: Services ── */}
          {step === 2 && (
            <div style={{ animation: 'fadeUp 0.35s ease' }}>
              <StepLabel n={2} total={STEP_COUNT} />
              <h2 style={heading}>Which services do you pay for?</h2>
              <p style={subtext}>Select all that apply — Flow will only show what you can actually watch.</p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 10, marginBottom: 28,
              }}>
                {SERVICES.map(s => {
                  const on = selSvc.includes(s.id)
                  return (
                    <button key={s.id} onClick={() => toggleSvc(s.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: on ? `${s.color}1a` : 'rgba(255,255,255,0.03)',
                      border: `1.5px solid ${on ? s.color + '55' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 14, padding: '12px 14px',
                      cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 0.18s',
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                        background: on ? `${s.color}28` : 'rgba(255,255,255,0.06)',
                        border: `1px solid ${on ? s.color + '44' : 'rgba(255,255,255,0.08)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 800,
                        color: on ? s.color : 'rgba(255,255,255,0.3)',
                      }}>{s.logo}</div>
                      <div style={{ textAlign: 'left', minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700,
                          color: on ? '#dceaf2' : 'rgba(180,215,235,0.5)',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{s.name}</div>
                        {on && <div style={{ fontSize: 10, color: s.color, fontWeight: 700, marginTop: 1 }}>● Active</div>}
                      </div>
                      {on && (
                        <div style={{ marginLeft: 'auto', color: s.color, fontSize: 16, flexShrink: 0 }}>✓</div>
                      )}
                    </button>
                  )
                })}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setStep(1)} style={btnSecondary}>← Back</button>
                <button
                  onClick={() => setStep(3)}
                  disabled={selSvc.length === 0}
                  style={{ ...btnPrimary, flex: 1, opacity: selSvc.length === 0 ? 0.4 : 1 }}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Email (optional) ── */}
          {step === 3 && (
            <div style={{ animation: 'fadeUp 0.35s ease' }}>
              <StepLabel n={3} total={STEP_COUNT} />
              <h2 style={heading}>Save your profile?</h2>
              <p style={subtext}>
                Optional — enter your email to sync your services and region across devices.
                You won't receive marketing emails.
              </p>

              <div style={{ marginBottom: 20 }}>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setEmailError('') }}
                  onKeyDown={e => e.key === 'Enter' && handleSaveEmail()}
                  placeholder="you@example.com"
                  autoComplete="email"
                  style={{
                    width: '100%', background: 'rgba(255,255,255,0.04)',
                    border: emailError
                      ? '1.5px solid rgba(255,80,80,0.55)'
                      : '1.5px solid rgba(80,160,220,0.25)',
                    borderRadius: 12, padding: '14px 16px',
                    color: '#dceaf2', fontSize: 15, fontFamily: 'inherit',
                    outline: 'none', boxSizing: 'border-box',
                    transition: 'border-color 0.15s',
                  }}
                />
                {emailError && (
                  <div style={{ fontSize: 11, color: 'rgba(255,100,100,0.8)', marginTop: 6 }}>
                    {emailError}
                  </div>
                )}
              </div>

              {/* Summary of chosen settings */}
              <div style={{
                background: 'rgba(59,140,230,0.08)',
                border: '1px solid rgba(59,140,230,0.18)',
                borderRadius: 12, padding: '12px 14px', marginBottom: 24,
                fontSize: 12, color: 'rgba(180,215,235,0.7)', lineHeight: 1.55,
              }}>
                <strong style={{ color: '#a5d8e8' }}>{regionObj.flag} {regionObj.name}</strong>
                {' · '}
                <strong style={{ color: '#a5d8e8' }}>{selSvc.length} service{selSvc.length !== 1 ? 's' : ''}</strong>
                {' selected'}
              </div>

              <button onClick={handleSaveEmail} style={btnPrimary}>
                {email.trim() ? 'Save Profile & Start →' : 'Start Exploring →'}
              </button>
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button onClick={() => setStep(2)} style={btnSecondary}>← Back</button>
                <button
                  onClick={() => finish('')}
                  style={{ ...btnSecondaryWide, flex: 1 }}
                >
                  Skip email
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

function StepLabel({ n, total }) {
  return (
    <div style={{ fontSize: 11, color: 'rgba(93,176,212,0.7)', fontWeight: 700,
      letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>
      Step {n} of {total}
    </div>
  )
}

const heading = {
  fontSize: 22, fontWeight: 900, color: '#dceaf2',
  letterSpacing: '-0.4px', marginBottom: 6, lineHeight: 1.2,
}
const subtext = {
  fontSize: 13, color: 'rgba(180,215,235,0.65)', lineHeight: 1.55, marginBottom: 20,
}
const btnPrimary = {
  width: '100%', background: 'linear-gradient(135deg,#3b8ce6,#2a6db5)',
  border: 'none', borderRadius: 14, padding: '15px 0',
  color: '#fff', fontSize: 15, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: '0 6px 24px rgba(59,140,230,0.35)',
  WebkitTapHighlightColor: 'transparent',
  transition: 'opacity 0.15s',
}
const btnSecondary = {
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 14, padding: '15px 20px',
  color: 'rgba(180,215,235,0.7)', fontSize: 15, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
  WebkitTapHighlightColor: 'transparent',
  flexShrink: 0,
}
const btnSecondaryWide = {
  background: 'transparent', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 14, padding: '13px 0',
  color: 'rgba(180,215,235,0.45)', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center',
  WebkitTapHighlightColor: 'transparent',
  display: 'block', width: '100%',
}
